import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { Navigate, NavLink, Outlet } from 'react-router'

import { activeOrg, api, ApiError, type Me, type Membership } from './api'

/** Pages about the platform itself (aleva.se's public pages, ...). */
const PLATFORM_ONLY = new Set(['/pages', '/mapping', '/crawl', '/mcp-servers', '/forms', '/blocklist'])

/** Admins only (the API enforces it too). */
const ADMIN_ONLY = new Set(['/blocklist'])

/** aleva.se's own public site; not in an enterprise installation. */
const SAAS_ONLY = new Set(['/pages'])

const NAV: { to: string; label: string; end?: boolean }[] = [
  { to: '/', label: 'Sources', end: true },
  { to: '/runs', label: 'Runs' },
  { to: '/buckets', label: 'Buckets' },
  { to: '/extraction', label: 'Extraction' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/mapping', label: 'Mapping' },
  { to: '/endpoints', label: 'Endpoints' },
  { to: '/api-keys', label: 'API keys' },
  { to: '/dashboards', label: 'Dashboards' },
  { to: '/crawl', label: 'Web crawl' },
  { to: '/blocklist', label: 'Blocklist' },
  { to: '/mcp-servers', label: 'MCP registry' },
  { to: '/forms', label: 'Forms' },
  { to: '/pages', label: 'Public pages' },
  { to: '/audit', label: 'Audit log' },
  { to: '/org', label: 'Organisation' },
]

export function Layout() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 5 * 60_000 })
  const status = useQuery({ queryKey: ['status'], queryFn: api.status, refetchInterval: 15_000, enabled: me.isSuccess })

  if (me.error) {
    const status = me.error instanceof ApiError ? me.error.status : 0
    // A remembered org this login cannot act in: forget it and retry.
    if (status === 403 && activeOrg.get()) {
      activeOrg.set('')
      window.location.reload()
      return null
    }
    // Customers (app.aleva.se) log in with an emailed link.
    if (status === 401 && !location.hostname.startsWith('internal.')) return <Navigate to="/login" replace />
    return (
      <div className="gate">
        <h1>{status === 403 ? 'No access' : 'Not signed in'}</h1>
        <p>
          {status === 403
            ? 'Your Authentik account is not in aleva-viewer, aleva-editor or aleva-admin.'
            : `The admin API answered: ${me.error.message}`}
        </p>
      </div>
    )
  }

  return (
    <div className="shell">
      <aside className="sb">
        <div className="brand">
          <span className="mark" aria-hidden>
            <i />
            <i />
            <i />
            <i />
          </span>
          Aleva <span className="muted">internal</span>
        </div>
        <nav>
          {NAV.filter((n) => !me.data || ((me.data.org_id === 'aleva' || !PLATFORM_ONLY.has(n.to)) && (me.data.edition !== 'enterprise' || !SAAS_ONLY.has(n.to)) && (me.data.roles.includes('admin') || !ADMIN_ONLY.has(n.to)))).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'item on' : 'item')}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="spacer" />
        <ThemeToggle />
        {status.data && <WorkerHealth ok={status.data.worker_ok} running={status.data.running.length} queued={status.data.queued.length} />}
        {me.data && <OrgSwitch me={me.data} />}
        {me.data && (
          <div className="who" title={me.data.groups.join(', ')}>
            <span>{me.data.name}</span>
            <span className="muted mono">
              {me.data.super ? 'super-user · ' : ''}
              {me.data.roles.includes('admin') ? 'admin' : me.data.roles.includes('editor') ? 'editor' : 'viewer'}
            </span>
            {me.data.customer && (
              <button className="btn btn-sm" onClick={() => api.logout().finally(() => window.location.assign('/login'))}>
                Log out
              </button>
            )}
          </div>
        )}
      </aside>
      <main className="main">
        {me.data?.licence?.message && (
          <div className={`callout ${me.data.licence.state === 'expiring' || me.data.licence.state === 'grace' ? 'warn' : 'err'} licence-banner`} role="status">
            Licence: {me.data.licence.message}
          </div>
        )}
        <Outlet />
      </main>
    </div>
  )
}

function WorkerHealth({ ok, running, queued }: { ok: boolean; running: number; queued: number }) {
  return (
    <NavLink to="/runs" className="health" title="Worker heartbeat (from /admin-api/status)">
      <span className={`dot ${ok ? 'ok' : 'bad'}`} aria-hidden />
      {ok ? 'Worker up' : 'Worker down'}
      {(running > 0 || queued > 0) && <span className="muted mono">· {running} running, {queued} queued</span>}
    </NavLink>
  )
}

declare global {
  interface Window {
    alevaApplyTheme?: () => void
  }
}

// The applied theme (html[data-theme]), which /theme.js also changes when
// the OS preference does.
function subscribeTheme(cb: () => void) {
  const mo = new MutationObserver(cb)
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => mo.disconnect()
}
const currentTheme = () => document.documentElement.dataset.theme ?? 'dark'

/** Light/dark switch: saves the choice; /theme.js applies it (and the OS preference until one is saved). */
function ThemeToggle() {
  const theme = useSyncExternalStore(subscribeTheme, currentTheme)
  const next = theme === 'dark' ? 'light' : 'dark'
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-pressed={theme === 'dark'}
      onClick={() => {
        try {
          localStorage.setItem('aleva-internal-theme', next)
        } catch {
          /* private mode: applies to this page only */
        }
        document.documentElement.dataset.theme = next
        document.documentElement.dataset.agThemeMode = next
        window.alevaApplyTheme?.()
      }}
    >
      {theme === 'dark' ? '☀ Light mode' : '☾ Dark mode'}
    </button>
  )
}

/** The acting org; members of several orgs and super-users switch here. */
function OrgSwitch({ me }: { me: Me }) {
  const qc = useQueryClient()
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: api.orgs, enabled: me.super || me.customer, staleTime: 60_000 })
  const list = (orgs.data ?? []).map((o) => ('org_id' in o ? { id: (o as Membership).org_id, name: (o as Membership).org_name } : { id: o.id, name: o.name }))
  const pick = (id: string) => {
    activeOrg.set(id === 'aleva' ? '' : id)
    qc.clear()
    window.location.assign('/')
  }
  if (list.length < 2) return <div className="org-tag mono" title="The organisation you act in">{me.org_id}</div>
  return (
    <label className="org-switch">
      <span className="muted">Organisation</span>
      <select value={me.org_id} onChange={(e) => pick(e.target.value)}>
        {list.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} ({o.id})
          </option>
        ))}
      </select>
    </label>
  )
}
